import { computed } from 'vue'
import { useAuth } from './useAuth'

export function useOrderEditPermissions() {
  const { user } = useAuth()

  /**
   * Check if an order can be edited based on its status and user role
   * @param {Object} order - The order object
   * @returns {Object} - { allowed: boolean, reason: string|null }
   */
  const canEditOrder = (order) => {
    if (!order) {
      return { allowed: false, reason: 'Order not found' }
    }

    // Super admins can always edit regardless of status
    if (user.value?.role === 'super_admin') {
      return { allowed: true, reason: null }
    }

    // Define statuses where regular users can edit
    const editableStatuses = ['pending', 'in_review']
    
    if (editableStatuses.includes(order.status)) {
      return { allowed: true, reason: null }
    }

    // Return specific reason for restriction
    const restrictedStatusMessages = {
      'quality_check': 'Orders in Quality Check cannot be edited. Contact a super admin if changes are needed.',
      'ready_release': 'Orders Ready for Release cannot be edited. Contact a super admin if changes are needed.',
      'ready_for_release': 'Orders Ready for Release cannot be edited. Contact a super admin if changes are needed.',
      'completed': 'Completed orders cannot be edited. Contact a super admin if changes are needed.',
      'cancelled': 'Cancelled orders cannot be edited. Contact a super admin if changes are needed.'
    }

    return { 
      allowed: false, 
      reason: restrictedStatusMessages[order.status] || 'This order cannot be edited at its current status.' 
    }
  }

  /**
   * Get computed property for edit permission
   * @param {Ref} order - Reactive order reference
   * @returns {ComputedRef} - Computed edit permission
   */
  const getEditPermission = (order) => {
    return computed(() => canEditOrder(order.value))
  }

  /**
   * Check if user is super admin (for showing override warnings)
   * @returns {ComputedRef<boolean>}
   */
  const isSuperAdmin = computed(() => user.value?.role === 'super_admin')

  /**
   * Check if editing a restricted order (for super admin warnings)
   * @param {Object} order - The order object
   * @returns {boolean}
   */
  const isEditingRestrictedOrder = (order) => {
    if (!order || !isSuperAdmin.value) return false
    
    const editableStatuses = ['pending', 'in_review']
    return !editableStatuses.includes(order.status)
  }

  return {
    canEditOrder,
    getEditPermission,
    isSuperAdmin,
    isEditingRestrictedOrder
  }
}
